import _ from 'lodash';
import { computed, effect, Injectable, signal, TemplateRef, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { AutoComplete } from 'src/app/features/shared/controls/models/auto-complete.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { ITabDataTemplate } from '../models/tab-data-template';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { NavigationStackService } from 'src/app/features/shared/navigation-stack/services/navigation-stack.service';
import { TabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { _TabPageService } from './_tab-page.service';
import { claimName } from 'src/assets/common/enums/claim-name.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { pageMode, TPageMode } from 'src/app/infrastructure/models/page-mode';
import { tabKind } from '../models/tab-kind';

@Injectable()
export class TabListService
    extends _TabPageService
{
    // Override properties — from _CentralPageService
    override readonly pageMode$$ = this._pageMode$$.asReadonly();
    override readonly pageMode_initial$$ = this._pageMode_initial$$.asReadonly();
    override readonly pageMode_prev$$ = this._pageMode_prev$$.asReadonly();
    override readonly btnCancelData$$ = this._btnCancelData$$;
    override readonly btnDeleteData$$ = this._btnDeleteData$$;
    override readonly btnEditData$$ = this._btnEditData$$;
    override readonly btnSaveData$$ = this._btnSaveData$$;
    override readonly onCancelEmit$ = this._onCancelEmit$.asObservable();
    override readonly onDeleteEmit$ = this._onDeleteEmit$.asObservable();
    override readonly onEditEmit$ = this._onEditEmit$.asObservable();
    override readonly onSaveEmit$ = this._onSaveEmit$.asObservable();
    override readonly pageName_sectionDetails$$ = this._pageName_sectionDetails$$.asReadonly();
    override readonly pageName_sectionEdit$$ = this._pageName_sectionEdit$$.asReadonly();
    override readonly template_sectionDetails$$ = this._template_sectionDetails$$.asReadonly();
    override readonly template_sectionEdit$$ = this._template_sectionEdit$$.asReadonly();
    override readonly sectionDetails_buttons_template$$ = this._sectionDetails_buttons_template$$.asReadonly();
    override readonly sectionEdit_buttons_template$$ = this._sectionEdit_buttons_template$$.asReadonly();
    override readonly sectionDetails_extraButtons_template$$ = this._sectionDetails_extraButtons_template$$.asReadonly();
    override readonly sectionEdit_extraButtons_template$$ = this._sectionEdit_extraButtons_template$$.asReadonly();
    override readonly pageLayout$$ = this._pageLayout$$.asReadonly();
    override readonly sectionDetails_show$$ = this._sectionDetails_show$$;
    override readonly tabVisible$$ = this._tabVisible$$;
    override readonly hasEditSection$$ = this._hasEditSection$$;

    // Override properties — from _TabPageService
    override readonly pagerMode$$ = this._pagerMode$$.asReadonly();
    override readonly animating$$ = this._animating$$;
    override readonly photoShow$$ = this._photoShow$$.asReadonly();
    override readonly photoGuid$$ = this._photoGuid$$;
    override readonly photoVersion$$ = this._photoVersion$$;
    override readonly photoDropEnabled$$ = this._photoDropEnabled$$;
    override readonly photoDrop$ = this._photoDrop$.asObservable();
    override readonly photoUpload$ = this._photoUpload$.asObservable();
    override readonly photoListChanged$ = this._photoListChanged$.asObservable();
    override readonly sectionCompact_template$$ = this._template_sectionCompact$$.asReadonly();
    override readonly tabIsPanel$$ = computed(() => this.tabList$$()?.[this.tabIndex$$()]?.kind === tabKind.panel);

    // Zolang de pagina's eigen initiële load (route-resolver/foto/compact-sectie) nog bezig is, is
    // een lege __authList hieronder nog geen betrouwbaar eindoordeel: de eigen tab-claims kunnen in
    // een latere golf van diezelfde load nog binnenkomen. __authPageModeLock herstelt dat sowieso,
    // maar pas ná een zichtbare flits naar detailsPage en terug — deze gate voorkomt die flits.
    private readonly __loadingPageService = injectRequired(LoadingPageService);
    private readonly __navigationStackService = injectRequired(NavigationStackService);
    private __tabRequestSeq = untracked(() => this.__navigationStackService.tabRequest$$()?.seq ?? 0);

    private __swipeCoord?: [number, number] | null;
    private __swipeTime?: number | null;
    private __tabList$ = new BehaviorSubject<ITabDataTemplate[]>([]);

    // Section-height presets (oplopend op .details-section-hoogte) + live drag-overlay
    private readonly __positions: TPageMode[] = [
        pageMode.listPage,    // 0      (tab maximaal)
        pageMode.tabPage,     // 8em
        pageMode.mediumPage,  // override ?? 50%
        pageMode.detailsPage, // 100%   (tab verborgen)
    ];
    private __dragHeight$$ = signal<number | null>(null);
    private __draggable$$ = signal<boolean>(false); // opt-in (sleeprand + header-swipe), gezet door tab-page
    private __dragging$$ = signal<boolean>(false);
    private __dropdownOpen$$ = signal<boolean>(false); // tabs-dropdown open: geen header-swipe (touch blijft op het openende element gebonden, óók tijdens scrollen door de opengeklapte lijst)
    private __locked$$ = signal<boolean>(false); // tab-positie vast: geen sleeprand, geen verticale swipe, geen reset/hide-knop
    private __positionRestored = false;
    private __presetPx: number[] = []; // [list, tab, medium, details] in px, aangeleverd door tab-page
    private __tabRestored = false;
    // true zolang pageMode.detailsPage hieronder een VOORLOPIGE gok is (claims voor de eigen tabs nog
    // niet compleet binnen), niet een bewuste keuze (record zonder tabs, of een expliciete
    // edit/details-navigatie). Onderscheidt "nog wachten" van "definitief geen tabs" bij de
    // detailsPage-sticky-check hieronder — zonder dit onderscheid blijft een pagina die tijdelijk
    // zonder autorisatie werd geëvalueerd permanent op detailsPage hangen, ook nadat de rest van de
    // claims (in golven) alsnog binnenkomen.
    private __authPageModeLock = false;

    dragging$$ = this.__dragging$$.asReadonly();
    locked$$ = this.__locked$$.asReadonly();
    sectionHeight$$ = computed<string>(() => {
        // Tijdens een actieve sleepbeweging volgt de hoogte altijd live de vinger/muis, ook
        // vóórdat de standwissel zelf is gecommit (zie height_dragCommit) — anders staat de
        // sleeprand een moment stil (dode zone) om vervolgens in één keer bij te springen zodra
        // de minimale sleepafstand wordt overschreden.
        if (this.__dragging$$()) {
            const _live = this.__dragHeight$$();
            if (_live != null) return `${_live}px`;
        }

        switch (this.pageMode$$()) {
            case pageMode.listPage:   return '0';
            case pageMode.tabPage:    return '8em';
            case pageMode.mediumPage: {
                const _live = this.__dragHeight$$();
                return _live != null ? `${_live}px` : '50%';
            }
            default:                    return '100%'; // details / edit / create
        }
    });

    // Sleeprand staat op een andere dan de pagina-eigen initiële zichtbare stand
    // (pageMode_initial$$, meestal tabPage)? Dan toont de reset-knop. Bij medium-page als
    // initiële stand (bv. Theme) verandert de resize-handle alleen de custom sleephoogte en
    // blijft de mode zelf 'medium-page' — dus die hoogte moet ook meetellen.
    tabPositionOffStandard$$ = computed(() => {
        if (!this.tabVisible$$()) return false;

        const _mode = this.pageMode$$();
        const _initial = this.pageMode_initial$$() ?? pageMode.tabPage;

        if (_mode !== _initial) return true;
        return _mode === pageMode.mediumPage && this.__dragHeight$$() != null;
    });

    hasList$$ = computed(() => this.tabList$$().length > 0);
    // override hier (i.p.v. in het "Override properties"-blok bovenin) omdat de veldinitializer
    // hasList$$ nodig heeft — class-fields initialiseren top-to-bottom, dus vóór hasList$$'s eigen
    // declaratie zou dit stil `undefined` zijn i.p.v. de echte signal.
    override readonly hasTabs$$ = this.hasList$$;
    isBeginList$$ = computed(() => this.tabIndex$$() === 0);
    isEndList$$ = computed(() => this.tabIndex$$() === this.tabList$$().length - 1);
    tabData$$ = computed(() => this.tabList$$()?.[this.tabIndex$$()] ?? null);
    tabIndex_first$$ = computed(() => 0);
    tabIndex_last$$ = computed(() => this.tabList$$().length - 1);
    tabIndex_next$$ = computed(() => this.tabIndex$$() >= this.tabIndex_last$$() ? null : this.tabIndex$$() + 1);
    tabIndex_previous$$ = computed(() => this.tabIndex$$() > 0 ? this.tabIndex$$() - 1 : null);
    tabLabel_next$$ = computed(() => this.tabIndex$$() < this.tabIndex_last$$() ? this.tabList$$()[this.tabIndex_next$$() ?? 0]?.label : '');
    tabLabel_previous$$ = computed(() => this.tabIndex$$() > this.tabIndex_first$$() ? this.tabList$$()[this.tabIndex_previous$$() ?? 0]?.label : '');
    tabListDropdown$$ = computed(() => this.tabList$$()?.map((tab, index) => AutoComplete.create(index, tab.label ?? null )));

    private __sourceList$$ = signal<ITabDataTemplate[]>([]);
    sourceList$$ = this.__sourceList$$.asReadonly();
    get SourceList() { return this.__sourceList$$(); }
    set SourceList(value: ITabDataTemplate[]) {
        this.__sourceList$$.set(value);
    }

    tabList$ = this.__tabList$.asObservable();
    tabList$$ = toSignal(this.__tabList$, {initialValue: []});
    get TabList() {return this.__tabList$.value};
    set TabList(tabList) {
        this.__tabList$.next(tabList);
    }

    private __tabIndex$$ = signal<number>(0);
    tabIndex$$ = this.__tabIndex$$.asReadonly();
    tabIndex$ = toObservable(this.tabIndex$$);
    get TabIndex() {return this.tabIndex$$()}
    set TabIndex(value) {
        LogService.envDev.logString('set tab to index', value);

        this.__tabIndex$$.set(value);
   }

    constructor()
    {
        super();

        // Persisteer de actieve tab generiek (panel + lijst). Vervangt de oude
        // koppeling via TabDataService.setTabData(); werkt ook voor tabs zonder tabData.
        effect(() => {
            const __list = this.tabList$$();
            const __index = this.tabIndex$$();
            const __name = __list?.[__index]?.name;
            if (!__name) return; // lijst nog leeg / geen geldige selectie
            if (!this.__tabRestored) return; // niet persisteren vóór de restore → opgeslagen tabName niet overschrijven met default-index 0
            untracked(() => this._tabPageStorageService.pageData$$.tabName.set(__name));
        });

        // Pas de laatst-actieve tab toe zodra de geautoriseerde tabList klaarstaat.
        // Apart van het sourceList-effect (dat pageMode schrijft en alleen op sourceList$$
        // reageert), zodat de opgeslagen tabName betrouwbaar wordt toegepast i.p.v. op de
        // default-index 0 te blijven hangen bij terugkeer naar de pagina.
        effect(() => {
            const __list = this.tabList$$();
            if (this.__tabRestored || !(__list?.length)) return;
            // Wacht op de user-scope-hydratie: vóór dat moment levert pageData$$() de lege fallback
            // (tabName null), waardoor we ten onrechte op index 0 zouden vastklikken en de opgeslagen
            // tabName via het persist-effect zouden overschrijven. Speelt op start-pages (bv. order),
            // die al renderen vóór het auth-token verwerkt is.
            if (!this._tabPageStorageService.hydrated$$()) return;

            this.__tabRestored = true;
            untracked(() => {
                const __tabName = this._tabPageStorageService.pageData$$().tabName;
                this.TabIndex = __tabName ? this.tabList_getIndex(__tabName) : 0;
            });
        });

        // Pas een tabName-wijziging ook ná de initiële restore toe (bv. NavigationHistoryService.presetTab
        // vanuit de drawer, geklikt terwijl deze pagina al open staat — router.navigateByUrl naar dezelfde
        // url is dan een no-op en het bovenstaande restore-effect draait maar één keer per bezoek).
        effect(() => {
            const __list = this.tabList$$();
            const __tabName = this._tabPageStorageService.pageData$$().tabName;
            if (!this.__tabRestored || !__list?.length || !__tabName) return;

            const __index = this.tabList_getIndex(__tabName);
            untracked(() => { if (this.TabIndex !== __index) this.TabIndex = __index; });
        });

        // Tab gevraagd vanuit de drawer terwijl deze pagina al open staat: het effect hierboven zet
        // alleen TabIndex, maar staan de tabs verborgen (detailsPage) of staat de pagina in Edit, dan
        // wisselde de tab onzichtbaar en leek de klik niets te doen. Hier dus ook de tabs tonen —
        // Edit verlaten zonder waarschuwing, net als Escape of wegnavigeren. Alleen aanvragen ná de
        // aanmaak van deze instantie (__tabRequestSeq) en voor het huidige frame tellen.
        effect(() => {
            const __request = this.__navigationStackService.tabRequest$$();
            const __list = this.tabList$$();
            if (!__request || __request.seq <= this.__tabRequestSeq || !this.__tabRestored || !__list?.length) return;

            untracked(() => {
                if (__request.frameId !== this.__navigationStackService.frameCurrent$$()?.id) return;

                this.__tabRequestSeq = __request.seq;
                this.TabIndex = this.tabList_getIndex(__request.tabName ?? undefined);
                if (!this.tabVisible$$()) this.position_restore();
            });
        });

        effect(() => {
            const __sourceList = this.sourceList$$();
            // Getrackt (niet untracked): een tab met ownerClaim leunt op de fire-and-forget
            // owner-claims-load (ApplicationUserService.ownerClaim_check) — zonder deze dependency
            // her-evalueert __sourceList_checkAuth nooit meer zodra die load ná de eerste check
            // binnenkomt, en blijft de tab onterecht verborgen.
            this._applicationUserService.ownerClaims$$();
            // Getrackt: zelfde reden als ownerClaims$$ hierboven. Bij een directe redirect naar een
            // startpagina na login (AccountPageService.__navigateAfterLogin) rendert deze pagina vóórdat
            // UserClaims binnen is — die komt pas via het antwoord van de pagina's EIGEN data-call
            // (response.interceptor.ts, LoginResponseDto zelf bevat geen claimsDto). Zonder deze
            // dependency her-evalueert de untracked check hieronder nooit meer zodra de claims alsnog
            // binnenkomen, en blijft de tab (en dus TablistFooter) permanent verborgen — pas een
            // volledig nieuwe page-instance (bv. via Home) kreeg tot nu toe de al-geladen claims mee.
            this._applicationUserService.userClaims$$();
            // Getrackt: zie __loadingPageService hierboven — een pageLoaded-transitie (na afloop van
            // de initiële load) moet deze check opnieuw laten draaien met de dan definitieve claims.
            const __pageStillLoading = this.__loadingPageService.isLoading$$();

            untracked(() => {
                const __hasSourceList = __sourceList.length > 0;
                const __userClaims = this._applicationUserService.UserClaims ?? [];
                const __hasUserClaims = __userClaims.length > 0;

                if (!__hasUserClaims) {
                    // Nog geen data om te checken (waarschijnlijk nog niet geladen, zie hierboven) —
                    // GEEN pageMode_set hier: dat zou (net als bij ownerClaims) een point-of-no-return
                    // zijn zodra __pageMode verderop bij een volgende run al detailsPage is. Gewoon
                    // wachten op de tracked userClaims$$-dependency hierboven.
                    LogService.envDev.warn('no data available to check authorization (yet)');
                    return;
                }

                // Tabs met ownerClaim leunen op de owner-set uit de response (ownerClaims$$). Zolang die nog
                // niet binnen is, de auth-check overslaan i.p.v. de tab als (voorlopig) niet-geautoriseerd te
                // behandelen: de detailsPage-tak hieronder is een point-of-no-return. Het effect tracked
                // ownerClaims$$, dus het herevalueert zodra de eerste response met owner-set binnenkomt.
                const __needsOwnerClaims = __sourceList.some(t => !!t.ownerClaim);
                if (__needsOwnerClaims && this._applicationUserService.ownerClaims$$() === null) {
                    return;
                }

                const __authList = this.__sourceList_checkAuth(__sourceList) ?? [];
                const __hasAuthList = __authList.length > 0;

                const __pageMode = this.pageMode$$();
                if (!__hasSourceList && __pageMode == pageMode.tabPage) {
                    if (__pageStillLoading) {
                        // Zelfde "wachten, niet vergrendelen" behandeling als de
                        // hasSourceList-maar-geen-auth-tak hieronder: een leeg sourceList$$ tijdens
                        // de eigen initiële load is nog geen bewuste "record zonder tabs"-keuze.
                        return;
                    }
                    this.__authPageModeLock = true;
                    this.pageMode_set(pageMode.detailsPage);
                    return;
                }
                if (__hasSourceList && !__hasAuthList && __pageMode == pageMode.tabPage) {
                    // GEEN __pageStillLoading-wachttak hier (in tegenstelling tot de !__hasSourceList-tak
                    // hierboven): __hasUserClaims en (indien nodig) ownerClaims$$() zijn op dit punt al
                    // bewezen geladen (de guards hierboven zouden anders al hebben ge-return't), dus dit
                    // is al een definitief oordeel. __pageStillLoading wordt alleen false zodra een tab
                    // zijn (onPageLoaded) emit — maar bij 0 geautoriseerde tabs mount er nooit een tab
                    // (zie ImageTablistComponent/DomainUserPhotoTablistComponent's ownerClaim-gate), dus
                    // die gebeurtenis komt structureel nooit: page bleef voor altijd op tabPage/loading
                    // hangen zodra iemand een pagina bekeek waarop alle tabs onbevoegd waren (bv. de
                    // DomainUser-pagina van een ander zonder Page_Read op de foto-tabs). Een latere
                    // claims-update triggert dit effect sowieso opnieuw (userClaims$$/ownerClaims$$ zijn
                    // al getrackte dependencies), en __authPageModeLock hieronder draait een voorbarige
                    // gok terug — de __pageStillLoading-check voegde dus geen extra zekerheid toe, enkel
                    // de deadlock.
                    LogService.prodDev.error('tablist is empty -missing authorization-');
                    this.__authPageModeLock = true;
                    this.pageMode_set(pageMode.detailsPage);
                    return;
                }
                if (__pageMode == pageMode.detailsPage) {
                    // Alleen sticky-blijven als detailsPage een bewuste stand is (record zonder tabs,
                    // of een expliciete edit/details-navigatie) — niet als de vorige run hierboven
                    // slechts een voorlopige gok was terwijl claims nog in golven binnenkwamen. Zonder
                    // deze uitzondering blijft de pagina na zo'n gok permanent op detailsPage staan,
                    // ook zodra __hasAuthList hierna alsnog true wordt.
                    if (!(this.__authPageModeLock && __hasAuthList)) {
                        this.pageMode_set(__pageMode);
                        return;
                    }
                    // Unlock: de gok bleek voorbarig, nu alsnog terug naar tabPage. __positionRestored
                    // hieronder overschrijft dit direct weer met een eventueel opgeslagen tabMode.
                    this.pageMode_set(pageMode.tabPage);
                }
                this.__authPageModeLock = false;

                if (__hasAuthList) {
                    // TabIndex is een kale array-index, geen tab-identiteit. Claims komen in golven
                    // binnen (zie de getrackte ownerClaims$$/userClaims$$-dependencies hierboven), dus
                    // __authList kan hier herhaaldelijk een ANDERE vorm krijgen (bv. een tab die alsnog
                    // wordt uitgesloten, waardoor alles erna één plek opschuift) terwijl de gebruiker
                    // allang een tab had gekozen. Zonder herkoppeling blijft TabIndex numeriek
                    // ongewijzigd en wijst hij na zo'n reflow stilzwijgend naar een ANDERE tab — die
                    // wordt dan door het persist-effect hierboven zonder waarschuwing als "actief
                    // gekozen" opgeslagen (zag AdminPanel na de Roles-tab → Role → terug de
                    // Activities-tab tonen, puur omdat 'functions' tussentijds wegviel uit de lijst).
                    const __selectedName = this.tabList$$()?.[this.tabIndex$$()]?.name;
                    this.TabList = this.__templates_carryOver(__authList);
                    if (this.__tabRestored && __selectedName) {
                        const __reindexed = this.tabList_getIndex(__selectedName);
                        if (this.TabIndex !== __reindexed) this.TabIndex = __reindexed;
                    }
                    // TabIndex-herstel bij de EERSTE toewijzing (nog geen __tabRestored) gebeurt in
                    // het aparte tabList$$-effect (zie constructor), zodat de opgeslagen tabName
                    // betrouwbaar wordt toegepast i.p.v. op de default-index 0 te blijven hangen.

                    // Eénmalig: laatst gekozen positie (swipe/sleep) herstellen uit localStorage
                    if (!this.__positionRestored) {
                        this.__positionRestored = true;

                        const __storedMode = this._tabPageStorageService.pageData$$().tabMode;
                        const __storedHeight = this._tabPageStorageService.pageData$$().tabHeight;
                        if (__storedMode != null) {
                            this.__dragHeight$$.set(__storedHeight);
                            this.pageMode_set(__storedMode);
                        } else {
                            // Nog geen opgeslagen stand: onthoud de initiële zichtbare stand (bv. Theme
                            // opent in medium-page), zodat een hide->show (position_restore) daarheen
                            // terugkeert i.p.v. de tabPage-fallback. Voor Home (initieel tabPage) is dit
                            // een no-op. De initiële mode komt uit _base-route-component (pageMode_set),
                            // niet via __position_apply, dus werd hij anders nooit als tabMode bewaard.
                            const __initialMode = this.pageMode$$();
                            if (__initialMode != null
                                && this.__positions.includes(__initialMode)
                                && __initialMode !== pageMode.detailsPage) {
                                this._tabPageStorageService.pageData$$.tabMode.set(__initialMode);
                            }
                        }
                    }
                }
            });
        });
    }

    private __sourceList_checkAuth(sourceList: ITabDataTemplate[] | null): ITabDataTemplate[] | null {
        const __hasSourceList = (sourceList?.length ?? 0) > 0;
        // Pagina zonder domain-scope (Home): de domain-fallback van routeKeys (het laatst bezochte domain) mag niet meetellen.
        const __routeKeys = this.tabAuth_noDomainScope ? null : this._activatedRouteService.routeKeys;

        if (!__hasSourceList) {return null}  // return on initialization

        return sourceList?.filter(tabData => {
            const __tabName = tabData.name;
            const __ownerClaim = tabData.ownerClaim;
            const __checked = this._applicationUserService.claim_check(__tabName, __routeKeys, claimName.page_Read)
                || (!!__ownerClaim && this._applicationUserService.ownerClaim_check(__ownerClaim.kind, __ownerClaim.ownerGuid, __tabName, claimName.page_Read));

            if (!__checked) {LogService.envDev.warn(`tab [${__tabName}]`, 'refused (no authorization)')}
            return __checked
        }) ?? null;
    }

    sourceList_add(tabData: ITabDataTemplate): void {
        const _sourceList = this.SourceList;
        let _tabData = _sourceList.find(item => item.name == tabData.name);

        if (_tabData) {
            _tabData = tabData;
        } else {
            _sourceList.push(tabData);
        }

        this.SourceList = [..._sourceList];
    }

    tab_next(): void {
        const _tabIndex = this.tabIndex$$();
        const _tabList = this.tabList$$();

        if (_tabIndex == null || !_tabList || _tabIndex >= _tabList.length - 1) {return;}

        this.TabIndex =_tabIndex + 1;
    }

    tab_previous(): void {
        const _tabIndex = this.tabIndex$$();
        if (!_tabIndex || _tabIndex <= 0) {return;}

        this.TabIndex = _tabIndex - 1;
    }

    tab_swipe(e: TouchEvent, when: string): void {
        const _swipe = this.__swipe_detect(e, when);

        if (_swipe?.axis === 'h') {
            _swipe.dir < 0 ? this.tab_next() : this.tab_previous();
        }
    }

    header_swipe(e: TouchEvent, when: string): void {
        if (!this.__draggable$$() || this.__locked$$() || this.__dropdownOpen$$()) {return;} // opt-in: alleen op [draggable], niet [locked], niet met open tabs-dropdown

        const _swipe = this.__swipe_detect(e, when);

        if (_swipe?.axis === 'v') {
            this.position_step(_swipe.dir < 0 ? 'up' : 'down');
        }
    }

    draggable_set(value: boolean): void {
        this.__draggable$$.set(value);
    }

    dropdownOpen_set(value: boolean): void {
        this.__dropdownOpen$$.set(value);
    }

    locked_set(value: boolean): void {
        this.__locked$$.set(value);
    }

    footer_swipe(e: TouchEvent, when: string): void {
        const _swipe = this.__swipe_detect(e, when);
        if (!_swipe) {return;}

        if (_swipe.axis === 'h') {
            _swipe.dir < 0 ? this.tab_next() : this.tab_previous();
        } else {
            if (this.__locked$$()) {return;} // [locked]: geen verticale positie-swipe
            // omhoog = terug naar laatst geregistreerde stand (gelijk aan Escape/show-toggle);
            // omlaag = verbergen. De échte reset naar standaard is de reset-knop.
            _swipe.dir < 0 ? this.position_restore() : this.position_hide();
        }
    }

    // 'up' = tab omhoog / details kleiner (richting list-page); 'down' = richting details-page
    position_step(dir: 'up' | 'down'): void {
        const _positions = this.__positions;
        const _custom = this.__dragHeight$$();

        // Custom sleephoogte: snap naar de eerstvolgende standaardpositie in de swipe-richting
        if (_custom != null && this.__presetPx.length === _positions.length) {
            this.__position_apply(this.__position_snap(_custom, dir), null);
            return;
        }

        const _current = _positions.indexOf(this.pageMode$$() as TPageMode);
        const _base = _current === -1 ? _positions.length - 1 : _current; // edit/create ~ details
        const _next = Math.max(0, Math.min(_positions.length - 1, _base + (dir === 'up' ? -1 : 1)));

        this.__position_apply(_positions[_next], null);
    }

    // tab-page levert de pixel-hoogtes van de presets (voor het snappen vanuit een custom hoogte)
    presetGeometry_set(containerPx: number, tabPx: number): void {
        this.__presetPx = [0, tabPx, containerPx * 0.5, containerPx];
    }

    // Footer omlaag: tab verbergen (verborgen stand wordt niet onthouden)
    position_hide(): void {
        this.__position_apply(pageMode.detailsPage, null);
    }

    // Sleeprand/positie terug naar de pagina-eigen initiële stand (meestal tabPage, 8em; bv.
    // mediumPage bij Theme); custom hoogte wissen.
    position_reset(): void {
        this.__position_apply(this.pageMode_initial$$() ?? pageMode.tabPage, null);
    }

    // Footer omhoog (en _TabPageRouteComponent._emit_onEscape, dus ook Escape op een
    // details-only sectie): terug naar de laatst opgeslagen zichtbare stand. Die opslag is
    // pagina-type-breed (niet per bezocht record), dus zonder deze guard zou Escape op bv. een
    // ANDER DomainUser zonder geautoriseerde tabs alsnog de tabPage-stand van een eerder bezoek
    // (met wél tabs, bv. je eigen DomainUser) toepassen — een lege tab-strook tonen die er niet
    // hoort te zijn.
    position_restore(): void {
        if (!this.hasList$$()) return;

        const _mode = this._tabPageStorageService.pageData$$().tabMode ?? pageMode.tabPage;
        const _height = this._tabPageStorageService.pageData$$().tabHeight;

        this.__dragHeight$$.set(_height);
        this.pageMode_set(_mode);
    }

    // Zet alleen de live visuele hoogte en [dragging] aan — bewust GEEN pageMode_set hier. De
    // sleeprand moet vanaf de allereerste pointer-beweging meebewegen (geen dode zone), maar de
    // standwissel zelf (tabPage -> mediumPage) mag pas na een minimale sleepafstand echt
    // plaatsvinden (zie height_dragCommit) — anders wisselt een enkele klik al van stand.
    height_dragStart(heightPx: number): void {
        if (!this.hasList$$()) return; // geen tabs: sleeprand hoort dan niet bereikbaar te zijn

        this.__dragging$$.set(true);
        this.__dragHeight$$.set(heightPx);
    }

    height_dragMove(heightPx: number): void {
        this.__dragHeight$$.set(heightPx);
    }

    // Pas nadat de minimale sleepafstand is overschreden: commit de standwissel zelf.
    height_dragCommit(): void {
        if (!this.hasList$$()) return;
        this.pageMode_set(pageMode.mediumPage);
    }

    // Sleepbeweging die de minimale afstand haalde: standwissel + hoogte definitief toepassen.
    height_dragEnd(): void {
        this.__dragging$$.set(false);
        this.__position_apply(pageMode.mediumPage, this.__dragHeight$$());
    }

    // Sleepbeweging die de minimale afstand NIET haalde (een klik): alleen de visuele
    // sleeptoestand ongedaan maken, geen standwissel en niets onthouden. __dragHeight$$ bewust
    // NIET terugzetten naar null: bij een initiële mediumPage-stand (bv. Theme) staat daar de
    // reeds geldende custom hoogte in (height_dragStart mat exact die waarde) — nullen zou die
    // hoogte laten terugspringen naar de default 50% bij een simpele klik zonder verplaatsing.
    height_dragCancel(): void {
        this.__dragging$$.set(false);
    }

    // Eerstvolgende preset t.o.v. de custom hoogte, in de swipe-richting
    private __position_snap(customPx: number, dir: 'up' | 'down'): TPageMode {
        const _px = this.__presetPx;       // [list, tab, medium, details]
        const _pos = this.__positions;

        if (dir === 'down') {
            for (let i = 0; i < _px.length; i++) {
                if (_px[i] > customPx + 1) {return _pos[i];}
            }
            return _pos[_pos.length - 1];
        }

        for (let i = _px.length - 1; i >= 0; i--) {
            if (_px[i] < customPx - 1) {return _pos[i];}
        }
        return _pos[0];
    }

    // Positie toepassen + onthouden in localStorage (mode + optionele custom sleephoogte).
    // Alleen zichtbare standen (list/tab/medium) worden onthouden; de verborgen stand niet.
    private __position_apply(mode: TPageMode, heightPx: number | null): void {
        // Zonder (geautoriseerde) tabs mag geen enkele actie een tab-onthullende stand aannemen —
        // anders toont bv. de reset-knop of een sleepbeweging een lege tab-strook op een
        // detailpagina zonder tabs (zie ook position_restore hieronder voor hetzelfde probleem
        // via Escape).
        const __mode = (mode !== pageMode.detailsPage && !this.hasList$$()) ? pageMode.detailsPage : mode;

        this.__dragHeight$$.set(heightPx);
        this.pageMode_set(__mode);

        if (this.__positions.includes(__mode) && __mode !== pageMode.detailsPage) {
            this._tabPageStorageService.pageData$$.tabMode.set(__mode);
            this._tabPageStorageService.pageData$$.tabHeight.set(heightPx);
        }
    }

    private __swipe_detect(e: TouchEvent, when: string): { axis: 'h' | 'v', dir: 1 | -1 } | null {
        const _coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
        const _time = new Date().getTime();

        if (when === 'start') {
            this.__swipeCoord = _coord;
            this.__swipeTime = _time;
            return null;
        }

        if (!this.__swipeCoord || !this.__swipeTime) {return null;}

        const _dx = _coord[0] - this.__swipeCoord[0];
        const _dy = _coord[1] - this.__swipeCoord[1];
        const _duration = _time - this.__swipeTime;

        if (_duration >= 1000) {return null;}                                       // Quick enough
        if (Math.abs(_dx) > 30 && Math.abs(_dx) > Math.abs(_dy * 3)) {               // Horizontaal genoeg
            return { axis: 'h', dir: _dx < 0 ? -1 : 1 };
        }
        if (Math.abs(_dy) > 30 && Math.abs(_dy) > Math.abs(_dx * 3)) {               // Verticaal genoeg
            return { axis: 'v', dir: _dy < 0 ? -1 : 1 };
        }

        return null;
    }

    // Footer- en knop-templates registreert elke lijst-tab maar één keer (na zijn eerste render, zie
    // _SourceList_Base_ToolComponent._ngAfterViewInit) op zijn TabList-object. Bouwt de pagina haar tabs
    // daarna opnieuw op (bv. Home na __onboarding_check: alleen nog de domains-tab, als nieuw object), dan
    // staan die templates niet op de nieuwe objecten. De tab-inhoud wordt op index gevolgd (tab-page
    // trackById), dus een tab met dezelfde naam op dezelfde index houdt zijn component — en registreert
    // zich niet opnieuw: zonder overname verdween zo de hele footer incl. de add-knop. Een tab op een andere
    // index krijgt een nieuw component dat zichzelf weer registreert; daar dus niets overnemen.
    private __templates_carryOver(tabList: ITabDataTemplate[]): ITabDataTemplate[] {
        const __previous = this.TabList ?? [];

        tabList.forEach((tab, index) => {
            const __prev = __previous[index];
            if (!__prev || __prev === tab || __prev.name !== tab.name) return;

            tab.templateFooter ??= __prev.templateFooter;
            tab.templateButtons ??= __prev.templateButtons;
        });

        return tabList;
    }

    tabList_addContentButtons(tabName: TabName, templateButtons: TemplateRef<any> | null): void {
        let _tabList = [...this.TabList];
        const _tabIndex = this.tabList_getIndex(tabName);
        const _hasTemplate = templateButtons != null;
        const _hasIdex = _tabIndex != -1 && Array.isArray(_tabList) && _tabIndex < _tabList.length;

        if (_hasIdex && _hasTemplate) {
            // const _currTemplate = _tabList[_tabIndex].templateButtons;
            // if (!_currTemplate) {
                let _tabData = _tabList[_tabIndex];
                _tabData.templateButtons = templateButtons;
                _tabList[_tabIndex]= _tabData;
                this.TabList = _tabList;
            // }
        }
    }

    tabList_addFooter(tabName: TabName, templateFooter?: TemplateRef<any> | null): void {
        let _tabList = [...this.TabList];
        const _tabIndex = this.tabList_getIndex(tabName);
        const _hasTemplate = templateFooter != null;
        const _hasIdex = _tabIndex != -1 && Array.isArray(_tabList) && _tabIndex < _tabList.length;

        if (_hasIdex && _hasTemplate) {
            let _tabData = _tabList[_tabIndex];
            _tabData.templateFooter = templateFooter;
            _tabList[_tabIndex]= _tabData;
            this.TabList = _tabList;
        }
    }

    tabList_getIndex(tabName?: TabName): number {
        const _tabList = this.TabList;
        const _tabName = tabName ?? '';
        let _tabIndex: number = 0;

        if (Array.isArray(_tabList) && _tabName.length > 0) {
            const _found = _tabList.findIndex(tabData => tabData && tabData.name === _tabName);
            if (_found !== -1) _tabIndex = _found;
        }

        return _tabIndex;
    }

    // Broadcast voor tabs die gemount blijven terwijl ze niet de actieve tab zijn (bv. onder
    // preserveContent op de mat-tab-group). needRefresh hierboven wordt normaal alleen gelezen bij
    // een nieuwe mount (zie _list.service.ts._list_load$) — zonder dit signaal zou een niet-actieve
    // maar wél bewaarde tab het never meer oppikken. _source-list-base-feature.component.ts luistert
    // hierop en herlaadt zichzelf zodra hij niet de actieve tab is.
    private readonly _tabRefresh$ = new Subject<void>();
    readonly tabRefresh$ = this._tabRefresh$.asObservable();

    tabList_setRefresh(value: boolean): void {
        const __tabList = this.tabList$$();

        __tabList.forEach(item => item.needRefresh = value);

        if (value) this._tabRefresh$.next();
    }
}
